import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Tariff, TariffComponent, TariffComponentType } from "../types";
import { useToast } from "../toast";

interface Props {
  open: boolean;
  tariff: Tariff | null;
  onClose: () => void;
  onSaved: () => void;
}

interface TariffForm {
  name: string;
  currency: string;
  description: string;
  is_active: boolean;
  components: TariffComponent[];
}

const newComponent = (currency: string): TariffComponent => ({
  type: "energy",
  price: 0,
  currency,
  start_after_minutes: 0,
  time_ranges: [],
});

const emptyForm: TariffForm = {
  name: "",
  currency: "EUR",
  description: "",
  is_active: true,
  components: [],
};

export default function TariffDialog({ open, tariff, onClose, onSaved }: Props) {
  const api = useAuthApi();
  const [form, setForm] = useState<TariffForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    if (tariff) {
      setForm({
        name: tariff.name,
        currency: tariff.currency || "EUR",
        description: tariff.description ?? "",
        is_active: tariff.is_active,
        components: tariff.components ?? [],
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, tariff]);

  const updateComponent = (index: number, patch: Partial<TariffComponent>) => {
    setForm((prev) => ({
      ...prev,
      components: prev.components.map((component, i) =>
        i === index ? { ...component, ...patch } : component
      ),
    }));
  };

  const removeComponent = (index: number) => {
    setForm((prev) => ({
      ...prev,
      components: prev.components.filter((_, i) => i !== index),
    }));
  };

  const addComponent = () => {
    setForm((prev) => ({
      ...prev,
      components: [...prev.components, newComponent(prev.currency || "EUR")],
    }));
  };

  const addTimeRange = (componentIndex: number) => {
    setForm((prev) => ({
      ...prev,
      components: prev.components.map((component, index) =>
        index === componentIndex
          ? {
              ...component,
              time_ranges: [...(component.time_ranges ?? []), { start_time: "00:00", end_time: "00:00" }],
            }
          : component
      ),
    }));
  };

  const removeTimeRange = (componentIndex: number, rangeIndex: number) => {
    setForm((prev) => ({
      ...prev,
      components: prev.components.map((component, index) =>
        index === componentIndex
          ? {
              ...component,
              time_ranges: (component.time_ranges ?? []).filter((_, i) => i !== rangeIndex),
            }
          : component
      ),
    }));
  };

  const updateTimeRange = (
    componentIndex: number,
    rangeIndex: number,
    field: "start_time" | "end_time",
    value: string
  ) => {
    setForm((prev) => ({
      ...prev,
      components: prev.components.map((component, index) =>
        index === componentIndex
          ? {
              ...component,
              time_ranges: (component.time_ranges ?? []).map((range, i) =>
                i === rangeIndex ? { ...range, [field]: value } : range
              ),
            }
          : component
      ),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        currency: (form.currency || "EUR").trim().toUpperCase(),
        description: form.description || null,
        is_active: form.is_active,
        components: form.components.map((component) => ({
          type: component.type,
          price: Number(component.price),
          currency: (component.currency || form.currency || "EUR").trim().toUpperCase(),
          start_after_minutes: Number(component.start_after_minutes || 0),
          time_ranges: (component.time_ranges ?? []).map((range) => ({
            start_time: range.start_time,
            end_time: range.end_time,
          })),
        })),
      };
      if (tariff) {
        await api(`${MGMT}/tariffs/${tariff.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        toast.show("Tariff updated");
      } else {
        await api(`${MGMT}/tariffs`, {
          method: "POST",
          body: JSON.stringify(body),
        });
        toast.show("Tariff created");
      }
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{tariff ? "Edit Tariff" : "Add Tariff"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label="Default Currency"
            placeholder="EUR"
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
          />
          <TextField
            label="Description"
            placeholder="Optional notes…"
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
            }
            label="Active"
          />

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">Components</Typography>
            <Button startIcon={<AddIcon />} onClick={addComponent}>
              Add component
            </Button>
          </Stack>

          {form.components.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Add at least one component (e.g. energy price).
            </Typography>
          )}

          {form.components.map((component, componentIndex) => (
            <Stack
              key={`${component.type}-${componentIndex}`}
              spacing={1}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={component.type}
                    onChange={(e) =>
                      updateComponent(componentIndex, { type: e.target.value as TariffComponentType })
                    }
                  >
                    <MenuItem value="energy">Energy</MenuItem>
                    <MenuItem value="parking">Parking</MenuItem>
                    <MenuItem value="idle">Idle</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Price"
                  type="number"
                  size="small"
                  inputProps={{ step: 0.001 }}
                  value={component.price}
                  onChange={(e) =>
                    updateComponent(componentIndex, { price: parseFloat(e.target.value) || 0 })
                  }
                />
                <TextField
                  label="Currency"
                  size="small"
                  value={component.currency}
                  onChange={(e) => updateComponent(componentIndex, { currency: e.target.value.toUpperCase() })}
                />
                <IconButton color="error" onClick={() => removeComponent(componentIndex)}>
                  <DeleteIcon />
                </IconButton>
              </Stack>
              <TextField
                label="Start after minutes"
                type="number"
                size="small"
                inputProps={{ min: 0, step: 1 }}
                value={component.start_after_minutes}
                onChange={(e) =>
                  updateComponent(componentIndex, { start_after_minutes: parseFloat(e.target.value) || 0 })
                }
              />

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Time ranges (optional)
                </Typography>
                <Button size="small" onClick={() => addTimeRange(componentIndex)}>
                  Add range
                </Button>
              </Stack>

              {(component.time_ranges ?? []).map((range, rangeIndex) => (
                <Stack key={`${componentIndex}-${rangeIndex}`} direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    label="Start"
                    type="time"
                    value={range.start_time}
                    onChange={(e) => updateTimeRange(componentIndex, rangeIndex, "start_time", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    size="small"
                    label="End"
                    type="time"
                    value={range.end_time}
                    onChange={(e) => updateTimeRange(componentIndex, rangeIndex, "end_time", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <IconButton color="error" onClick={() => removeTimeRange(componentIndex, rangeIndex)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={saving || !form.name.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
